import { useEffect, useMemo, useState } from "react";
import { Trophy, Lock, Check, Zap, Target, Users, Gauge, Medal, Flame, Pin, PinOff } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import { MassExtinctionPreview } from "../components/MassExtinctionPreview";
import SkinPreview from "../components/SkinPreview";
import api from "../lib/api";

const TIER_STYLE = {
  bronze: {
    border: "border-[#c78c4a]/60",
    text: "text-[#e8a969]",
    glow: "shadow-[0_0_12px_rgba(199,140,74,0.25)]",
    badge: "bg-[#c78c4a]/15",
  },
  silver: {
    border: "border-[#c0c0d5]/60",
    text: "text-[#d8d8ea]",
    glow: "shadow-[0_0_12px_rgba(192,192,213,0.25)]",
    badge: "bg-[#c0c0d5]/15",
  },
  gold: {
    border: "border-[#ffd700]/70",
    text: "text-[#ffd700]",
    glow: "shadow-[0_0_14px_rgba(255,215,0,0.3)]",
    badge: "bg-[#ffd700]/15",
  },
  legendary: {
    border: "border-[#b026ff]/70",
    text: "text-[#d58bff]",
    glow: "shadow-[0_0_16px_rgba(176,38,255,0.35)]",
    badge: "bg-[#b026ff]/15",
  },
};

const CATEGORIES = [
  { id: "all", label: "All", icon: Trophy },
  { id: "progression", label: "Progression", icon: Target, metrics: ["levels_completed"] },
  { id: "sector", label: "Sector", icon: Medal, metrics: ["sector_1_levels", "sector_2_levels", "sector_3_levels", "sector_4_levels", "sector_5_levels", "sector_6_levels", "unlocked_level_max"] },
  { id: "score", label: "Score", icon: Zap, metrics: ["total_score"] },
  { id: "combat", label: "Combat", icon: Gauge, metrics: ["perfect_runs", "max_balls", "powerups_collected", "blocks_destroyed"] },
  // Bosses — every Leviathan/sector-boss/sovereign trophy lives here so
  // pilots can quickly see their boss-rank long-tail goals.
  { id: "bosses", label: "Bosses", icon: Flame, metrics: [
    "bosses_killed",
    "sector_bosses_defeated",
    "space_worm_kills",
    "skeleton_worm_kills",
    "boss_1_killed", "boss_2_killed", "boss_3_killed", "boss_4_killed", "boss_5_killed", "boss_6_killed",
    "boss_s_ranks_sector_1", "boss_s_ranks_sector_2", "boss_s_ranks_sector_3", "boss_s_ranks_sector_4", "boss_s_ranks_sector_5", "boss_s_ranks_sector_6",
  ] },
  { id: "speed", label: "Speed", icon: Gauge, metrics: ["speed_runs"] },
  { id: "social", label: "Social", icon: Users, metrics: ["friends_count", "daily_streak", "perfect_months"] },
];

function categoryOf(metric) {
  for (const c of CATEGORIES) {
    if (c.id === "all") continue;
    if (c.metrics?.includes(metric)) return c.id;
  }
  return "progression";
}

function formatValue(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`;
  return String(n);
}

export default function Achievements() {
  const [data, setData] = useState(null);
  const [skinCatalog, setSkinCatalog] = useState({});
  const [filter, setFilter] = useState("all");
  // Currently-pinned achievement id (or null). Mirrored from `data` but
  // kept as a separate state so the pin toggle can update instantly
  // without a full refetch round-trip.
  const [pinnedId, setPinnedId] = useState(null);

  useEffect(() => {
    let cancel = false;
    api
      .get("/achievements")
      .then((r) => {
        if (cancel) return;
        setData(r.data);
        setPinnedId(r.data?.pinned_achievement_id || null);
      })
      .catch(() => !cancel && setData({ catalog: [], unlocked: [], stats: {} }));
    // Ball-skins catalog — needed to look up `reward_skin` IDs and render
    // a small live preview canvas on every trophy-skin card. Populated as
    // a `{ id: skin }` map for O(1) lookups during render.
    api
      .get("/ball-skins")
      .then((r) => {
        if (cancel) return;
        const map = {};
        for (const s of r.data?.skins || []) map[s.id] = s;
        setSkinCatalog(map);
      })
      .catch(() => {});
    return () => {
      cancel = true;
    };
  }, []);

  const togglePin = (aid) => {
    // Toggle: clicking the currently-pinned trophy clears it, clicking
    // anything else replaces the pin. Optimistic update so the UI feels
    // instant; rollback on server error.
    const next = pinnedId === aid ? null : aid;
    const prev = pinnedId;
    setPinnedId(next);
    api
      .post("/achievements/pin", { achievement_id: next })
      .then(() => {
        if (next) {
          const meta = data?.catalog?.find?.((a) => a.id === next);
          toast.success("Trophy pinned", {
            description: `${meta?.name || next} now appears next to your name on leaderboards.`,
            icon: <Pin className="w-4 h-4 text-[#ffd700]" />,
            duration: 4000,
          });
        } else {
          toast("Trophy unpinned", {
            icon: <PinOff className="w-4 h-4 text-white/60" />,
            duration: 2500,
          });
        }
      })
      .catch((err) => {
        setPinnedId(prev);
        toast.error("Couldn't pin trophy", {
          description: err?.response?.data?.detail || "Please try again.",
          duration: 3500,
        });
      });
  };

  const unlockedMap = useMemo(() => {
    const m = new Map();
    (data?.unlocked || []).forEach((u) => m.set(u.id, u.unlocked_at));
    return m;
  }, [data]);

  const featuredSet = useMemo(
    () => new Set(data?.featured || []),
    [data],
  );
  const featuredMultiplier = data?.featured_multiplier || 2;
  const featuredItems = useMemo(() => {
    if (!data) return [];
    return (data.featured || [])
      .map((id) => data.catalog.find((c) => c.id === id))
      .filter(Boolean);
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const cat = data.catalog;
    if (filter === "all") return cat;
    return cat.filter((a) => categoryOf(a.metric) === filter);
  }, [data, filter]);

  const unlockedCount = data?.unlocked?.length || 0;
  const totalCount = data?.catalog?.length || 0;
  const totalBonus = useMemo(() => {
    if (!data) return 0;
    return (data.unlocked || []).reduce((sum, u) => {
      const a = data.catalog.find((c) => c.id === u.id);
      return sum + (a?.reward_score || 0);
    }, 0);
  }, [data]);

  if (!data) {
    return (
      <div className="relative min-h-screen">
        <div className="starfield" />
        <Navbar />
        <main className="relative z-10 flex items-center justify-center min-h-screen font-mono-cosmic text-[#8b949e] tracking-widest">
          LOADING ACHIEVEMENTS...
        </main>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <div className="starfield" />
      <Navbar />
      <main className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 fade-in" data-testid="achievements-page">
        <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-mono-cosmic text-xs tracking-[0.35em] text-[#00ffff] uppercase mb-2">
              // PILOT · TROPHY CASE
            </div>
            <h1 className="font-heading font-black uppercase text-3xl sm:text-5xl tracking-tighter">
              Achievements
            </h1>
            <p className="text-white/60 text-sm mt-1">
              Milestones, speed-runs, sector clears & social badges. Every unlock pays out a score bonus.
            </p>
          </div>
          <div className="glass px-5 py-3 flex items-center gap-6" data-testid="achievements-summary">
            <div>
              <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/50">
                Unlocked
              </div>
              <div className="font-heading font-black text-lg text-[#00ff41]" data-testid="achievements-unlocked-count">
                {unlockedCount} / {totalCount}
              </div>
            </div>
            <div className="w-px h-8 bg-white/15" />
            <div>
              <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/50">
                Bonus Score
              </div>
              <div className="font-heading font-black text-lg text-[#ffd700]" data-testid="achievements-bonus-score">
                +{totalBonus.toLocaleString()}
              </div>
            </div>
          </div>
        </header>

        {featuredItems.length > 0 && (
          <section
            className="mb-8 glass p-5 border border-[#ff6b1a]/50"
            style={{ boxShadow: "0 0 18px rgba(255,107,26,0.25)" }}
            data-testid="weekly-missions"
          >
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 font-mono-cosmic text-[10px] tracking-[0.35em] text-[#ff6b1a] uppercase mb-1">
                  <Flame className="w-3.5 h-3.5" />
                  Weekly Missions · {featuredMultiplier}× Bonus
                </div>
                <div className="font-heading uppercase text-lg sm:text-xl tracking-tight text-white">
                  Unlock any of these this week for double score
                </div>
              </div>
              <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/40">
                Rotates Mon 00:00 UTC
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {featuredItems.map((a) => {
                const isDone = unlockedMap.has(a.id);
                const stat = data.stats?.[a.metric] || 0;
                const progress = Math.min(1, stat / a.target);
                return (
                  <div
                    key={a.id}
                    className="relative px-4 py-3 border border-[#ff6b1a]/35 bg-[#ff6b1a]/5"
                    data-testid={`featured-${a.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-heading uppercase text-sm tracking-tight text-white mb-1">
                          {a.name}
                        </div>
                        <div className="text-white/55 text-xs mb-2 leading-snug min-h-[28px]">
                          {a.desc}
                        </div>
                      </div>
                      {/* Cosmetic preview — currently only Worm Tamer carries
                          a non-skin cosmetic reward, so we render the
                          inline gold-worm crawler the moment its card
                          rotates into the weekly featured trio. */}
                      {a.id === "worm-tamer" && (
                        <MassExtinctionPreview size={56} />
                      )}
                    </div>
                    <div className="relative h-1 bg-white/10 mb-1 overflow-hidden">
                      <div
                        className={`absolute inset-y-0 left-0 ${isDone ? "bg-[#00ff41]" : "bg-[#ff6b1a]"}`}
                        style={{ width: `${progress * 100}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between font-mono-cosmic text-[9px] tracking-widest uppercase">
                      <span className="text-white/50">
                        {formatValue(Math.min(stat, a.target))} / {formatValue(a.target)}
                      </span>
                      <span className="text-[#ff6b1a]">
                        +{(a.reward_score * featuredMultiplier).toLocaleString()} pts
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="achievements-category-tabs">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const active = filter === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setFilter(c.id)}
                className={`px-3 py-1.5 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase border transition inline-flex items-center gap-2 ${
                  active
                    ? "border-[#00ffff] text-[#00ffff] bg-[#00ffff]/10"
                    : "border-white/15 text-white/60 hover:text-white"
                }`}
                data-testid={`achievements-cat-${c.id}`}
              >
                <Icon className="w-3 h-3" />
                {c.label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((a) => {
            const stat = data.stats?.[a.metric] || 0;
            const progress = Math.min(1, stat / a.target);
            const unlocked = unlockedMap.has(a.id);
            const style = TIER_STYLE[a.tier] || TIER_STYLE.bronze;
            return (
              <div
                key={a.id}
                className={`glass p-4 border ${style.border} ${unlocked ? style.glow : "opacity-80"}`}
                data-testid={`achievement-card-${a.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className={`px-2 py-0.5 ${style.badge} font-mono-cosmic text-[8px] tracking-[0.3em] uppercase ${style.text}`}>
                    {a.tier}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {featuredSet.has(a.id) && !unlocked && (
                      <div
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#ff6b1a] text-[#ff6b1a] font-mono-cosmic text-[8px] tracking-widest uppercase"
                        data-testid={`ach-featured-${a.id}`}
                      >
                        <Flame className="w-2.5 h-2.5" /> {featuredMultiplier}×
                      </div>
                    )}
                    {unlocked ? (
                      <>
                        <button
                          onClick={() => togglePin(a.id)}
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 border font-mono-cosmic text-[8px] tracking-widest uppercase transition ${
                            pinnedId === a.id
                              ? "border-[#ffd700] text-[#ffd700] bg-[#ffd700]/15"
                              : "border-white/20 text-white/50 hover:text-[#ffd700] hover:border-[#ffd700]/60"
                          }`}
                          data-testid={`ach-pin-${a.id}`}
                          title={pinnedId === a.id ? "Unpin from profile" : "Pin to profile"}
                        >
                          {pinnedId === a.id ? (
                            <>
                              <Pin className="w-2.5 h-2.5 fill-current" /> Pinned
                            </>
                          ) : (
                            <Pin className="w-2.5 h-2.5" />
                          )}
                        </button>
                        <div className="inline-flex items-center gap-1 text-[#00ff41]" data-testid={`ach-unlocked-${a.id}`}>
                          <Check className="w-3.5 h-3.5" />
                          <span className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase">Unlocked</span>
                        </div>
                      </>
                    ) : (
                      <Lock className="w-3.5 h-3.5 text-white/40" />
                    )}
                  </div>
                </div>
                <div className="font-heading uppercase text-base tracking-tight text-white mb-1">
                  {a.name}
                </div>
                <div className="text-white/60 text-xs leading-snug mb-3 min-h-[32px]">
                  {a.desc}
                </div>
                <div className="relative h-1.5 bg-white/10 overflow-hidden mb-2" data-testid={`ach-progress-${a.id}`}>
                  <div
                    className={`absolute inset-y-0 left-0 ${unlocked ? "bg-[#00ff41]" : "bg-[#00ffff]"}`}
                    style={{ width: `${progress * 100}%` }}
                  />
                </div>
                <div className="flex items-center justify-between font-mono-cosmic text-[10px] tracking-widest uppercase">
                  <span className="text-white/55" data-testid={`ach-progress-text-${a.id}`}>
                    {formatValue(Math.min(stat, a.target))} / {formatValue(a.target)}
                  </span>
                  <span className={style.text}>+{a.reward_score.toLocaleString()} pts</span>
                </div>
                {a.reward_skin && (() => {
                  // Live skin preview — animated mini canvas of the
                  // exact comet skin this trophy unlocks. Falls back to
                  // a label-only badge when the skin catalog hasn't
                  // loaded yet (or the skin is missing — e.g. legacy
                  // achievements before the catalog grew).
                  const skin = skinCatalog[a.reward_skin];
                  return (
                    <div
                      className="mt-2 pt-2 border-t border-white/10"
                      data-testid={`ach-trophy-skin-${a.id}`}
                    >
                      <div className="flex items-center gap-3">
                        {skin ? (
                          <SkinPreview skin={skin} size={44} aspect="square" />
                        ) : (
                          <Trophy className="w-3 h-3 text-[#00ff41]" />
                        )}
                        <div className="flex flex-col min-w-0 flex-1">
                          <div className="font-mono-cosmic text-[8px] tracking-[0.3em] uppercase text-[#00ff41]/70 leading-tight">
                            Trophy Skin
                          </div>
                          <div
                            className="font-heading text-[11px] uppercase tracking-tight text-[#00ff41] truncate"
                            title={skin?.name || a.reward_skin}
                          >
                            {skin?.name || a.reward_skin}
                          </div>
                          {!unlocked && (
                            <div className="font-mono-cosmic text-[8px] tracking-[0.25em] uppercase text-white/35 mt-0.5">
                              Locked · unlock to equip
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                {a.reward_cosmetic && (
                  <div
                    className="mt-2 pt-2 border-t border-white/10 flex items-center gap-1.5 font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-[#ffea3a]"
                    style={{ textShadow: "0 0 6px rgba(255,234,58,0.4)" }}
                    data-testid={`ach-cosmetic-${a.id}`}
                    title="Cosmetic reward — auto-applies the moment this achievement unlocks"
                  >
                    <Trophy className="w-3 h-3" />
                    <span className="truncate">{a.reward_cosmetic}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
